import { useId, useEffect, type ReactElement } from "react";
import {
  Activity,
  ChartArea,
  ChartColumn,
  ChartLine,
  Hash,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import { CHART_TYPES, type KpiChartParams } from "@playbookai/widget-schemas";
import { RadioGroup } from "../../atoms/RadioGroup/RadioGroup.js";
import { Checkbox } from "../../atoms/Checkbox/Checkbox.js";
import { useKpiCatalog } from "../kpi-catalog.js";
import { KpiPickList } from "../KpiPickList.js";
import type { DashboardWidgetEditorProps } from "../types.js";

type ChartType = (typeof CHART_TYPES)[number];

const CHART_ICON: Record<ChartType, LucideIcon> = {
  line: ChartLine,
  area: ChartArea,
  bar: ChartColumn,
  "big-number": Hash,
  "mini-stat": Activity,
  trend: TrendingUp,
};

const CHART_LABEL: Record<ChartType, string> = {
  line: "Line",
  area: "Area",
  bar: "Bar",
  "big-number": "Big number",
  "mini-stat": "Mini stat",
  trend: "Trend",
};

const LEGEND =
  "block text-dense-2 uppercase tracking-wider text-pb-text-muted font-semibold mb-1.5";

/**
 * Per-instance editor for `singleKpi` / `chart` (rendered in the dashboard edit
 * dialog). Data-blind: the KPI pick-list comes from the host-provided
 * `useKpiCatalog()` context — `null` (no provider) renders an empty-state.
 */
export function KpiChartEditor({
  draftParams,
  onChange,
}: DashboardWidgetEditorProps): ReactElement {
  const catalog = useKpiCatalog();
  const params = draftParams as KpiChartParams;
  const activateCatalog = catalog?.activate;
  const chartType: ChartType = params.chartType ?? "line";
  const set = (patch: Partial<KpiChartParams>): void =>
    onChange({ ...params, ...patch });
  useEffect(() => {
    activateCatalog?.(params.kpiId ? [params.kpiId] : []);
  }, [activateCatalog, params.kpiId]);

  const pickerName = useId();
  return (
    <div className="space-y-4">
      <fieldset>
        <legend className={LEGEND}>Choose a KPI</legend>
        <KpiPickList
          name={pickerName}
          selectedId={params.kpiId}
          onSelect={(kpiId) => set({ kpiId })}
        />
      </fieldset>

      <fieldset>
        <legend className={LEGEND}>Display as</legend>
        <RadioGroup
          aria-label="Display as"
          value={chartType}
          onChange={(value) => set({ chartType: value as ChartType })}
          className="grid grid-cols-2 sm:grid-cols-3 gap-3"
          options={CHART_TYPES.map((value) => {
            const Icon = CHART_ICON[value];
            return {
              value,
              label: CHART_LABEL[value],
              icon: <Icon className="h-4 w-4" />,
            };
          })}
        />
      </fieldset>

      <fieldset className="space-y-3">
        <legend className={LEGEND}>Display options</legend>
        <div className="grid gap-3">
          <Checkbox
            checked={params.showDelta ?? true}
            onChange={(v) => set({ showDelta: v })}
            label="Show change"
          />
          <Checkbox
            checked={params.showCommentary ?? true}
            onChange={(v) => set({ showCommentary: v })}
            label="Show AI commentary"
          />
        </div>
      </fieldset>
    </div>
  );
}
